import {NgModule} from '@angular/core';
import {BrowserModule} from '@angular/platform-browser';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {HttpClientModule} from '@angular/common/http';
import {RouterModule} from '@angular/router';

import {AuthGuard} from '@app/app/shared/guards/auth.guard';
import {JwtModule} from '@auth0/angular-jwt';
import {environment} from '@app/environments/environment';

import {SharedModule} from '@app/app/shared/shared.module';
import {MainRoutingModule} from '@app/app/main/main.routing.module';
import {MainRootComponent} from '@app/app/main/components/main-root.component';
import {MainComponent} from '@app/app/main/components/main/main.component';
import {HeaderComponent} from '@app/app/main/components/header/header.component';
import {SidebarComponent} from '@app/app/main/components/sidebar/sidebar.component';
import {FooterComponent} from '@app/app/main/components/footer/footer.component';
import {NavComponent} from '@app/app/main/components/nav/nav.component';
import {SearchFormComponent} from '@app/app/main/components/nav/search-form/search.form';
import {NavFilterListComponent} from '@app/app/main/components/nav/filter-list/nav-filter-list.component';
import {NavFilterItemComponent} from '@app/app/main/components/nav/filter-item/nav-filter-item.component';
import {MapWidgetComponent} from '@app/app/main/components/widgets/map/map-widget.component';
import {PreloaderComponent} from '@app/app/shared/components/preloader/preloader.component';
import {MessageService} from '@app/app/shared/services/message.service';
import {NgxSmartModalModule, NgxSmartModalService} from 'ngx-smart-modal';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {LoginComponent} from '@app/app/login/login.component';
import {AuthenticationService} from '@app/app/shared/services/authentication.service';
import {ForgotPasswordComponent} from '@app/app/forgot-password/forgot.password.component';
import {ResetPasswordComponent} from '@app/app/reset-password/reset.password.component';
import {MatTableModule} from '@angular/material/table';
import {MatPaginatorModule} from '@angular/material/paginator';
import {MatSortModule} from '@angular/material/sort';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatCheckboxModule, MatDatepickerModule, MatInputModule, MatNativeDateModule} from '@angular/material';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatDialogModule} from '@angular/material/dialog';
import {MatButtonModule} from '@angular/material/button';
import {MatRadioModule} from '@angular/material/radio';
import {MatSelectModule} from '@angular/material/select';
import {MatButtonToggleModule} from '@angular/material/button-toggle';
import {MatTabsModule} from '@angular/material/tabs';
import {ManufacturerComponent} from '@app/app/manufacturer/manufacturer.component';
import {ControllerComponent} from '@app/app/controller/controller.component';
import {ControllerDialogComponent} from '@app/app/controller/controller-dialog';
import {ProfileComponent} from '../profile/profile.component';
import {ChangePasswordDialogComponent} from '@app/app/profile/change-password-dialog.component';
import {BsDropdownModule} from 'ngx-bootstrap/dropdown';
import {AdminGuard} from '@app/app/shared/guards/admin.guard';
import {AdminOrClientGuard} from '@app/app/shared/guards/admin.or.client.guard';
import {AdminOrManufacturerGuard} from '@app/app/shared/guards/admin.or.manufacturer.guard';
import {NavigationComponent} from '@app/app/shared/components/navigation/navigation.component';
import {ParksComponent} from '@app/app/dashboard/parks/parks.component';
import {ClockComponent} from '@app/app/dashboard/parks/park/clock/clock.component';
import {ParkInfoFormComponent} from '@app/app/dashboard/forms/park-info-form/park-info-form.component';
import {ParkFeatureLoopItemComponent} from '@app/app/dashboard/parks/park/controls/filtration/feature-loop/feature-loop-item/park-feature-loop-item.component';
import {ParkFilterComponent} from '@app/app/dashboard/parks/park/filter/park-filter.component';
import {ParkStatisticActivatorsComponent} from '@app/app/dashboard/parks/park/statistic/activators/park-statistic-activators.component';
import {ParkScheduleItemComponent} from '@app/app/dashboard/parks/park/schedules/park-schedule-item/park-schedule-item.component';
import {ParkWeatherComponent} from '@app/app/dashboard/parks/park/weather/park-weather.component';
import {ParkTasksComponent} from '@app/app/dashboard/parks/park/tasks/park-tasks.component';
import {ParkBackwashComponent} from '@app/app/dashboard/parks/park/backwash/park-backwash.component';
import {ParkSchedulesComponent} from '@app/app/dashboard/parks/park/schedules/park-schedules.component';
import {ParkActivatorControlsComponent} from '@app/app/dashboard/parks/park/controls/features/activators/park-activator-controls.component';
import {ParkSlidedownComponent} from '@app/app/dashboard/parks/park/slidedown/park-slidedown.component';
import {ParkSettingsFormComponent} from '@app/app/dashboard/forms/park-settings-form/park-settings-form.component';
import {ParkPurgeComponent} from '@app/app/dashboard/parks/park/purge/park-purge.component';
import {ParkBypassControlsComponent} from '@app/app/dashboard/parks/park/controls/others/bypass/park-bypass-controls.component';
import {ParkFeatureLoopControlsComponent} from '@app/app/dashboard/parks/park/controls/filtration/feature-loop/park-feature-loop-controls.component';
import {ParkListComponent} from '@app/app/dashboard/parks/park-list/park-list.component';
import {ParkFiltrationLoopControlsComponent} from '@app/app/dashboard/parks/park/controls/filtration/filtration-loop/park-filtration-loop-controls.component';
import {ParkSwitcherComponent} from '@app/app/dashboard/parks/park/switcher/park-switcher.component';
import {ParkMeterComponent} from '@app/app/dashboard/parks/park/meter/park-meter.component';
import {ParkFilterItemComponent} from '@app/app/dashboard/parks/park/filter/filter-item/park-filter-item.component';
import {ParkTaskFormComponent} from '@app/app/dashboard/forms/park-task-form/park-task-form.component';
import {ParkFeatureControlsComponent} from '@app/app/dashboard/parks/park/controls/features/features/park-feature-controls';
import {ParkTasksItemComponent} from '@app/app/dashboard/parks/park/tasks/park-tasks-item/park-tasks-item.component';
import {ParkAlarmsItemComponent} from '@app/app/dashboard/parks/park/alarms/message/park-alarms-item.component';
import {ParkSettingsComponent} from '@app/app/dashboard/parks/park/settings/park-settings.component';
import {ParkFeatureControlComponent} from '@app/app/dashboard/parks/park/controls/features/features/control/park-feature-control.component';
import {ParkFiltrationLoopItemComponent} from '@app/app/dashboard/parks/park/controls/filtration/filtration-loop/filtration-loop-item/park-filtration-loop-item.component';
import {ParkScheduleFormComponent} from '@app/app/dashboard/forms/park-schedule-form/park-schedule-form.component';
import {ParkInfoViewComponent} from '@app/app/dashboard/parks/park/settings/info/park-info-view.component';
import {ParkSettingsViewComponent} from '@app/app/dashboard/parks/park/settings/view/park-settings-view.component';
import {ParkComponent} from '@app/app/dashboard/parks/park/park.component';
import {ParkStatisticWaterConsumptionComponent} from '@app/app/dashboard/parks/park/statistic/water-consumption/park-statistic-water-consumption.component';
import {DatePickerComponent} from '@app/app/dashboard/parks/park/date-picker/date-picker.component';
import {ParkAlarmsComponent} from '@app/app/dashboard/parks/park/alarms/park-alarms.component';
import {ParkActivatorControlComponent} from '@app/app/dashboard/parks/park/controls/features/activators/control/park-activator-control.component';
import {OwlDateTimeModule, OwlNativeDateTimeModule} from 'ng-pick-datetime';
import {BsDatepickerModule} from 'ngx-bootstrap/datepicker';
import {TimepickerModule} from 'ngx-bootstrap/timepicker';
import {NgbModule} from '@ng-bootstrap/ng-bootstrap';
import {AlarmsComponent} from '@app/app/alarms/alarms.component';
import {ParkNotificationsFormComponent} from '@app/app/dashboard/forms/park-notifications-form/park-notifications-form.component';
import {ParkNotificationsViewComponent} from '@app/app/dashboard/parks/park/settings/notifications/park-notifications-view.component';
import {ReportsComponent} from '@app/app/reports/reports.component';
import {ManufacturersComponent} from '@app/app/manufacturers/manufacturers.component';
import {ManufacturerDialogComponent} from '@app/app/manufacturers/manufacturer.dialog.component';
import {AdminsDialogComponent} from '@app/app/admins/admins.dialog.component';
import {AdminsComponent} from '@app/app/admins/admins.component';
import {ClientsDialogComponent} from '@app/app/clients/clients.dialog.component';
import {ClientsComponent} from '@app/app/clients/clients.component';
import {MatToolbarModule} from '@angular/material/toolbar';
import {EmployeesComponent} from '@app/app/employees/employees.component';
import {EmployeesDialogComponent} from '@app/app/employees/employees.dialog.component';
import {AgmJsMarkerClustererModule} from '@agm/js-marker-clusterer';
import {UpdateControllerDialogComponent} from '@app/app/controller/update.controller.dialog.component';
import {SaveFilterStateService} from '@app/app/shared/services/save-filter-state.service';
import {ReportsCrowdComponent} from '@app/app/reports/crowd/reports.crowd.component';
import {ParksFetcher} from '@app/app/shared/helpers/parks.fetcher';
import {ReportsAlarmsComponent} from '@app/app/reports/alarms/reports.alarms.component';
import {ReportsHardwareComponent} from '@app/app/reports/hardware/reports.hardware.component';
import {ReportsMapComponent} from '@app/app/reports/map/reports.map.component';
import {CustomMapComponent} from '@app/app/reports/map/components/custom-map/custom-map.component';
import {NgxEchartsModule} from 'ngx-echarts';
import {UrlHelper} from '@app/app/shared/helpers/url.helper';
import {PercentsPipe} from '@app/app/shared/helpers/percents.pipe';
import {ReportsConsoComponent} from '@app/app/reports/conso/reports.conso.component';
import {UcfirstPipe} from '@app/app/shared/helpers/ucfirst.pipe';
import {CommandsService} from '@app/app/shared/services/commands.service';
import {AdminOrManufacturerOrClientGuard} from '@app/app/shared/guards/admin-or-manufacturer-or-client-guard.service';
import {AdminOrClientOrEmployeeGuard} from '@app/app/shared/guards/admin.or.client.or.employee.guard';
import {MatExpansionModule} from '@angular/material/expansion';
import {UploadLogoComponent} from '@app/app/main/components/sidebar/upload-logo/upload-logo.component';
import {ParkBypassItemComponent} from '@app/app/dashboard/parks/park/controls/others/bypass/bypass-item/park-bypass-item.component';
import {ParkRainDiverterControlsComponent} from '@app/app/dashboard/parks/park/controls/others/rain_diverter/park-rain-diverter-controls.component';
import {ParkRainDiverterItemComponent} from '@app/app/dashboard/parks/park/controls/others/rain_diverter/rain-diverter-item/park-rain-diverter-item.component';
import {DegreesPipe} from '@app/app/shared/helpers/degrees.pipe';
import {DecimalPipe} from '@angular/common';
import {ProUserGuard} from '@app/app/shared/guards/pro.user.guard';
import {Card2Component} from '@app/app/dashboard/parks/park/cards/card.2.component';
import {Card3Component} from '@app/app/dashboard/parks/park/cards/card.3.component';
import {ProvisionControllerDialogComponent} from '@app/app/controller/provision.controller.dialog.component';
import {ToggleLedDialogComponent} from '@app/app/controller/toggle.led.dialog.component';
import {ParkLedTimeViewComponent} from '@app/app/dashboard/parks/park/settings/led-time/park-led-time-view.component';
import {ParkLedTimeFormComponent} from '@app/app/dashboard/forms/park-led-time/park-led-time-form.component';
import {ParkClockFormComponent} from '@app/app/dashboard/forms/park-clock/park-clock-form.component';
import {ParkActivatorLogicControlsComponent} from '@app/app/dashboard/parks/park/controls/features/activators-logic/park-activator-logic-controls.component';
import {ParkActivatorLogicControlComponent} from '@app/app/dashboard/parks/park/controls/features/activators-logic/control/park-activator-logic-control.component';
import {LitersPipe} from '@app/app/shared/helpers/liters.pipe';
import {IsArrayPipe} from '@app/app/shared/helpers/isArray.pipe';
import {ParkRainSensorControlsComponent} from '@app/app/dashboard/parks/park/controls/others/rain_sensor/park-rain-sensor-controls.component';
import {ParkRainSensorItemComponent} from '@app/app/dashboard/parks/park/controls/others/rain_sensor/rain-sensor-item/park-rain-sensor-item.component';
import {ParkCustomSequencesComponent} from '@app/app/dashboard/parks/park/custom-sequences/park-custom-sequences.component';
import {CustomSequenceTracksComponent} from '@app/app/dashboard/parks/park/custom-sequences/tracks/custom-sequence-tracks.component';
import {CustomSequenceFeatureItemComponent} from '@app/app/dashboard/parks/park/custom-sequences/feature-item/custom-sequence-feature-item.component';
import {CustomSequenceFeatureControlComponent} from '@app/app/dashboard/parks/park/custom-sequences/feature-control/custom-sequence-feature-control.component';
import {ParkCustomSequenceFormComponent} from '@app/app/dashboard/forms/park-custom-sequence-form/park-custom-sequence-form.component';
import {ParkCustomSequenceLightFormComponent} from '@app/app/dashboard/forms/park-custom-sequence-light-form/park-custom-sequence-light-form.component';
import {AccessComponent} from '@app/app/access/access.component';
import {AccessItemComponent} from '@app/app/access/components/access-item/access-item.component';

import {SuccessSnackbarComponent} from '@app/app/shared/snackbars/snackbar-success/snackbar-success.component';
import {WarningSnackbarComponent} from '@app/app/shared/snackbars/snackbar-warning/snackbar-warning.component';

@NgModule({
  declarations: [
    MainRootComponent,
    MainComponent,
    HeaderComponent,
    SidebarComponent,
    FooterComponent,
    NavComponent,
    SearchFormComponent,
    NavFilterListComponent,
    NavFilterItemComponent,
    MapWidgetComponent,
    PreloaderComponent,
    LoginComponent,
    ForgotPasswordComponent,
    ResetPasswordComponent,
    AlarmsComponent,
    ManufacturerComponent,
    ControllerComponent,
    ControllerDialogComponent,
    ProfileComponent,
    ChangePasswordDialogComponent,
    NavigationComponent,
    ParksComponent,
    ParksComponent,
    ParkComponent,
    Card2Component,
    Card3Component,
    ParkListComponent,
    ParkSwitcherComponent,
    ParkSlidedownComponent,
    ParkWeatherComponent,
    ParkMeterComponent,
    ParkFilterComponent,
    ParkFilterItemComponent,
    ParkAlarmsComponent,
    ParkAlarmsItemComponent,
    ParkTasksComponent,
    ParkTasksItemComponent,
    ParkActivatorControlsComponent,
    ParkActivatorLogicControlsComponent,
    ParkFeatureControlsComponent,
    ParkFeatureLoopControlsComponent,
    ParkFiltrationLoopControlsComponent,
    ParkStatisticWaterConsumptionComponent,
    ParkBypassItemComponent,
    ParkStatisticActivatorsComponent,
    ParkSettingsComponent,
    ParkSettingsFormComponent,
    ParkBypassControlsComponent,
    ParkRainDiverterControlsComponent,
    ParkRainDiverterItemComponent,
    ParkRainSensorControlsComponent,
    ParkRainSensorItemComponent,
    ParkBackwashComponent,
    ParkPurgeComponent,
    ParkActivatorControlComponent,
    ParkActivatorLogicControlComponent,
    ParkFeatureControlComponent,
    ParkFeatureLoopItemComponent,
    ParkFiltrationLoopItemComponent,
    ParkTaskFormComponent,
    ParkSettingsViewComponent,
    DatePickerComponent,
    ParkScheduleFormComponent,
    ParkSchedulesComponent,
    ParkScheduleItemComponent,
    ParkInfoFormComponent,
    ClockComponent,
    ParkClockFormComponent,
    ParkInfoViewComponent,
    ParkLedTimeViewComponent,
    ParkLedTimeFormComponent,
    ParkNotificationsFormComponent,
    ParkNotificationsViewComponent,
    ReportsComponent,
    ManufacturersComponent,
    ManufacturerDialogComponent,
    AdminsComponent,
    AdminsDialogComponent,
    ClientsComponent,
    ClientsDialogComponent,
    EmployeesComponent,
    EmployeesDialogComponent,
    UpdateControllerDialogComponent,
    ProvisionControllerDialogComponent,
    ToggleLedDialogComponent,
    ReportsCrowdComponent,
    ReportsAlarmsComponent,
    ReportsHardwareComponent,
    ReportsMapComponent,
    CustomMapComponent,
    PercentsPipe,
    DegreesPipe,
    LitersPipe,
    IsArrayPipe,
    ReportsConsoComponent,
    UcfirstPipe,
    UploadLogoComponent,
    ParkCustomSequencesComponent,
    CustomSequenceTracksComponent,
    CustomSequenceFeatureItemComponent,
    CustomSequenceFeatureControlComponent,
    ParkCustomSequenceFormComponent,
    ParkCustomSequenceLightFormComponent,
    SuccessSnackbarComponent,
    WarningSnackbarComponent,
    AccessComponent,
    AccessItemComponent
  ],
  imports: [
    BrowserModule,
    SharedModule.forRoot(),
    BrowserAnimationsModule,
    HttpClientModule,
    JwtModule.forRoot({
      config: {
        tokenGetter: tokenGetter,
        whitelistedDomains: environment.whitelistedDomains
      }
    }),
    RouterModule,
    MainRoutingModule,
    NgxSmartModalModule.forRoot(),
    ReactiveFormsModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    MatButtonModule,
    MatRadioModule,
    MatSelectModule,
    MatButtonToggleModule,
    MatTabsModule,
    MatToolbarModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatCheckboxModule,
    MatExpansionModule,
    BsDropdownModule.forRoot(),
    SharedModule,
    OwlDateTimeModule,
    OwlNativeDateTimeModule,
    NgbModule.forRoot(),
    TimepickerModule.forRoot(),
    BsDatepickerModule,
    AgmJsMarkerClustererModule,
    NgxEchartsModule,
    FormsModule,
  ],
  providers: [
    AuthGuard,
    AdminGuard,
    AdminOrClientGuard,
    AdminOrManufacturerOrClientGuard,
    AdminOrManufacturerGuard,
    AdminOrClientOrEmployeeGuard,
    ProUserGuard,
    MessageService,
    NgxSmartModalService,
    AuthenticationService,
    SaveFilterStateService,
    ParksFetcher,
    DecimalPipe,
    UrlHelper,
    CommandsService,
    PercentsPipe,
  ],
  bootstrap: [MainRootComponent],
  entryComponents: [
    ManufacturerDialogComponent,
    ControllerDialogComponent,
    ChangePasswordDialogComponent,
    AdminsDialogComponent,
    ClientsDialogComponent,
    EmployeesDialogComponent,
    UpdateControllerDialogComponent,
    ProvisionControllerDialogComponent,
    ToggleLedDialogComponent,
    SuccessSnackbarComponent,
    WarningSnackbarComponent
  ]
})
export class MainModule {
}

export function tokenGetter() {
  return localStorage.getItem('accessToken');
}



// WEBPACK FOOTER //
// ./src/app/main/main.module.ts